// =============================================================================
// /admin/obieg — „Organizacja › Obieg dokumentów” (UD-12a, plan 53 §11.2,
// D53-35). Ekran PRACY, nie sprzedaży: stoi poza strażnikiem samoobsługowej
// sprzedaży, więc widzą go także firmy fazy 1.
//
// Zakładka żyje w `?tab=`, tak jak w Drukowaniu i w Aplikacjach i urządzeniach:
// „Dokumenty” (UD-12a, tylko odczyt) i „Powiązania kart” (UD-22b — karty
// z Receptora czekające na decyzję). Nieznana zakładka w adresie to Dokumenty.
// Pasek zakładek widzi rola z prawem podglądu; zakładka Powiązania sama mówi
// kierownikowi, że rozstrzyga właściciel albo administrator, i wtedy NIE PYTA.
//
// Menu nie jest zabezpieczeniem. Pozycję widzi tylko rola z prawem podglądu,
// ale adres da się wpisać ręcznie — dlatego każda zakładka sama pokazuje stan
// „brak prawa” i wtedy NIE PYTA bazy ani usługi (baza oddałaby takiej osobie
// pustą listę, a pusta lista udawałaby „nie ma dokumentów”).
// =============================================================================

import { Button, Tabs, TabsContent, TabsList, TabsTrigger } from '@insolitum/ui';
import { FileStack, Link2, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useAuth } from '../../../providers/AuthProvider';
import { prawaObiegu } from '../devices/obiegDokumentow';

import { ZakladkaDokumenty } from './ZakladkaDokumenty';
import { ZakladkaPowiazania } from './ZakladkaPowiazania';

export const ZAKLADKI = ['dokumenty', 'powiazania'] as const;
type Zakladka = (typeof ZAKLADKI)[number];

function czytajZakladke(surowa: string | null): Zakladka {
  return (ZAKLADKI as readonly string[]).includes(surowa ?? '')
    ? (surowa as Zakladka)
    : 'dokumenty';
}

export function ObiegDokumentowPage() {
  const { currentOrganization, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const zakladka = czytajZakladke(searchParams.get('tab'));
  const prawa = prawaObiegu({
    rola: currentOrganization?.role,
    statusCzlonkostwa: currentOrganization?.status,
    superAdmin: user?.is_super_admin === true,
  });
  const organizationId = currentOrganization?.organization_id;
  // Przycisk w nagłówku podbija licznik, a zakładka odświeża wtedy kursory,
  // listę i liczbę — bez pokazywania ładowania, jak tik zegara.
  const [odswiezenie, setOdswiezenie] = useState(0);

  const ustawZakladke = (wartosc: string) => {
    const nastepne = new URLSearchParams(searchParams);
    nastepne.set('tab', wartosc);
    setSearchParams(nastepne, { replace: true });
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="pagehead">
        <div>
          <h1 className="pagehead__title flex items-center gap-2">
            <FileStack className="w-6 h-6 text-aurora-brand" />
            Obieg dokumentów
          </h1>
          <p className="pagehead__sub">Dokumenty i karty, które Universe odczytał z Receptora.</p>
        </div>
        {prawa.podglad && organizationId && (
          <div className="pagehead__actions">
            <Button variant="secondary" size="sm" onClick={() => setOdswiezenie((n) => n + 1)}>
              <RefreshCw className="w-4 h-4" />
              Odśwież
            </Button>
          </div>
        )}
      </div>

      <Tabs value={zakladka} onValueChange={ustawZakladke}>
        {prawa.podglad && (
          <TabsList className="overflow-x-auto">
            <TabsTrigger value="dokumenty" icon={<FileStack className="w-4 h-4" />}>
              Dokumenty
            </TabsTrigger>
            <TabsTrigger value="powiazania" icon={<Link2 className="w-4 h-4" />}>
              Powiązania kart
            </TabsTrigger>
          </TabsList>
        )}
        {/* TabsContent renderuje tylko aktywną zakładkę — nieaktywna nie pyta. */}
        <TabsContent value="dokumenty">
          <p className="mb-4 text-sm text-aurora-muted">
            Dokumenty z Receptora: PZ, PW, RW, MM, INW i WZ. Faktury i dokumenty z innych systemów
            tu nie trafiają. Ta strona tylko pokazuje — niczego nie zmienia.
          </p>
          <ZakladkaDokumenty
            organizationId={organizationId}
            prawa={prawa}
            odswiezenie={odswiezenie}
          />
        </TabsContent>
        <TabsContent value="powiazania">
          <ZakladkaPowiazania
            organizationId={organizationId}
            prawa={prawa}
            odswiezenie={odswiezenie}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
